import React, { useEffect, useMemo, useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const LEARN_MORE_MARKDOWN_PATH = "/content/my-trips-learn-more.en.md";

function renderMarkdown(markdown) {
  const blocks = [];
  const paragraphLines = [];
  let listItems = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    blocks.push({
      type: "paragraph",
      text: paragraphLines.join(" "),
    });
    paragraphLines.length = 0;
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push({
      type: "list",
      items: listItems,
    });
    listItems = [];
  };

  markdown.split(/\r?\n/).forEach((line) => {
    const text = line.trim();

    if (!text) {
      flushParagraph();
      flushList();
      return;
    }

    if (text.startsWith("# ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h1", text: text.slice(2).trim() });
      return;
    }

    if (text.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: text.slice(3).trim() });
      return;
    }

    if (text.startsWith("- ") || text.startsWith("* ")) {
      flushParagraph();
      listItems.push(text.slice(2).trim());
      return;
    }

    flushList();
    paragraphLines.push(text);
  });

  flushParagraph();
  flushList();

  return blocks.map((block, index) => {
    const key = `learn-more-${block.type}-${index}`;
    if (block.type === "h1") {
      return <h1 key={key}>{block.text}</h1>;
    }
    if (block.type === "h2") {
      return <h2 key={key}>{block.text}</h2>;
    }
    if (block.type === "list") {
      return (
        <ul key={key}>
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>{item}</li>
          ))}
        </ul>
      );
    }
    return <p key={key}>{block.text}</p>;
  });
}

export default function MyTripsLearnMoreModal({ onClose }) {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState("");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadLearnMoreContent() {
      try {
        setStatus("loading");
        const response = await fetch(LEARN_MORE_MARKDOWN_PATH, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load ${LEARN_MORE_MARKDOWN_PATH}`);
        }
        const text = await response.text();
        if (!cancelled) {
          setMarkdown(text);
          setStatus("ready");
        }
      } catch (error) {
        if (error?.name === "AbortError" || cancelled) return;
        setStatus("error");
      }
    }

    loadLearnMoreContent();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const renderedContent = useMemo(() => renderMarkdown(markdown), [markdown]);

  return (
    <div
      className="mytrips-learn-more-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mytrips-learn-more-title"
    >
      <div className="mytrips-learn-more-modal__backdrop" onClick={onClose} />
      <section className="mytrips-learn-more-modal__sheet">
        <header className="mytrips-learn-more-modal__header">
          <span className="mytrips-learn-more-modal__header-icon" aria-hidden="true">
            <HelpCircle size={22} strokeWidth={2.25} />
          </span>
          <div>
            <h2 id="mytrips-learn-more-title">
              {t("myTrips.learnMore.title")}
            </h2>
          </div>
          <button
            type="button"
            className="mytrips-learn-more-modal__close"
            aria-label={t("myTrips.learnMore.close")}
            onClick={onClose}
          >
            <X size={21} strokeWidth={2.3} aria-hidden="true" />
          </button>
        </header>

        <div className="mytrips-learn-more-modal__content">
          {status === "loading" ? (
            <p className="mytrips-learn-more-modal__state">
              {t("myTrips.learnMore.loading")}
            </p>
          ) : status === "error" ? (
            <p className="mytrips-learn-more-modal__state mytrips-learn-more-modal__state--error">
              {t("myTrips.learnMore.error")}
            </p>
          ) : (
            <div className="mytrips-learn-more-modal__markdown">
              {renderedContent}
            </div>
          )}
        </div>

        <footer className="mytrips-learn-more-modal__footer">
          <button
            type="button"
            className="mytrips-learn-more-modal__done"
            onClick={onClose}
          >
            {t("myTrips.learnMore.close")}
          </button>
        </footer>
      </section>
    </div>
  );
}
